import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getOrdersByUser, getTransactionsByUser, getRechargeRequestsByUser, toMoney } from "@/services/supabaseService";
import type { Order, Transaction, RechargeRequest } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).replace(/\//g, "-");
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"overview" | "transactions" | "orders">("overview");
  const [orders, setOrders] = useState<Order[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pendingRecharge, setPendingRecharge] = useState<RechargeRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    Promise.all([
      getOrdersByUser(user.id),
      getTransactionsByUser(user.id),
      getRechargeRequestsByUser(user.id),
    ])
      .then(([o, t, reqs]) => {
        setOrders(o);
        setTransactions(t);
        // Find the latest pending recharge request
        const latestPending = reqs
          .filter((r) => r.status === "待審核")
          .sort((a, b) => {
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return tb - ta;
          })[0];
        setPendingRecharge(latestPending || null);
      })
      .catch(() => {
        // silently fail
      })
      .finally(() => setIsLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-lock-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">請先登入分銷商帳號</p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 cursor-pointer"
          >
            前往登入
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const orderStats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === "待發貨").length,
    shipped: orders.filter((o) => o.status === "已發貨").length,
    completed: orders.filter((o) => o.status === "已完成").length,
  };

  const recentOrders = orders.slice(0, 5);
  const recentTransactions = transactions.slice(0, 5);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12">
          {/* Profile header */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-line text-amber-600 text-xl"></i>
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-gray-900">{user.name}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                  <span>{user.email}</span>
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    {user.level}
                  </span>
                  <span>加入時間 {fmtDate(user.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <i className="ri-wallet-3-line text-emerald-600 text-sm"></i>
                </div>
                <span className="text-sm text-gray-500">錢包餘額</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ${toMoney(user.balance).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-time-line text-amber-600 text-sm"></i>
                </div>
                <span className="text-sm text-gray-500">審核中</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">
                ${pendingRecharge ? toMoney(pendingRecharge.amount).toLocaleString() : toMoney(user.pending_balance).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-blue-600 text-sm"></i>
                </div>
                <span className="text-sm text-gray-500">累計訂單</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{user?.total_orders ?? 0}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 inline-flex">
            {[
              { key: "overview" as const, label: "總覽" },
              { key: "transactions" as const, label: "交易紀錄" },
              { key: "orders" as const, label: "我的訂單" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer ${
                  activeTab === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Quick actions */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {[
                  { label: "全部貨源", icon: "ri-shopping-bag-3-line", href: "/products", color: "bg-amber-50 text-amber-600" },
                  { label: "我的購物車", icon: "ri-shopping-cart-line", href: "/dashboard/cart", color: "bg-blue-50 text-blue-600" },
                  { label: "提現中心", icon: "ri-bank-card-line", href: "/dashboard/withdraw", color: "bg-red-50 text-red-500" },
                  { label: "實名認證", icon: "ri-id-card-line", href: "/dashboard/kyc", color: "bg-stone-50 text-stone-600" },
                  { label: "我的訂單", icon: "ri-file-list-3-line", href: "/dashboard/orders", color: "bg-purple-50 text-purple-600" },
                ].map((action) => (
                  <Link
                    key={action.label}
                    to={action.href}
                    className="bg-white rounded-xl border border-gray-100 p-4 hover:border-amber-200 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-3`}>
                      <i className={`${action.icon} text-lg`}></i>
                    </div>
                    <p className="text-sm font-medium text-gray-800">{action.label}</p>
                  </Link>
                ))}
              </div>

              {/* Recent Transactions */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">最近交易</h2>
                </div>
                {isLoading ? (
                  <div className="px-5 py-8 text-center text-gray-400 text-sm">載入中...</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {recentTransactions.map((t) => (
                      <div key={t.id} className="px-5 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-800">{t.description || ""}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{fmtDate(t.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${(Number(t.amount) || 0) > 0 ? "text-emerald-600" : "text-red-500"}`}>
                            {(Number(t.amount) || 0) > 0 ? "+" : "-"}${Math.abs(Number(t.amount) || 0).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-400">餘額 ${toMoney(t.balance).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Orders */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">最近訂單</h2>
                  <Link to="/dashboard/orders" className="text-xs text-amber-600 hover:underline cursor-pointer">查看全部</Link>
                </div>
                {isLoading ? (
                  <div className="px-5 py-8 text-center text-gray-400 text-sm">載入中...</div>
                ) : recentOrders.length === 0 ? (
                  <div className="px-5 py-8 text-center text-gray-400 text-sm">暫無訂單</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {recentOrders.map((order) => (
                      <div key={order.id} className="px-5 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{order.id}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            ${toMoney(order.total_amount).toLocaleString()}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          order.status === "待發貨"
                            ? "bg-amber-100 text-amber-700"
                            : order.status === "已發貨"
                            ? "bg-blue-100 text-blue-700"
                            : order.status === "已完成"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-600"
                        }`}>
                          {order.status === "待發貨" ? "已訂貨" : order.status === "已發貨" ? "已上架" : order.status === "已完成" ? "銷售完畢" : order.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transactions */}
          {activeTab === "transactions" && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-900">全部交易紀錄</h2>
              </div>
              {isLoading ? (
                <div className="px-5 py-8 text-center text-gray-400 text-sm">載入中...</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {transactions.map((t) => (
                    <div key={t.id} className="px-5 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          t.type === "儲值"
                            ? "bg-emerald-50"
                            : t.type === "體驗金回收"
                            ? "bg-amber-50"
                            : "bg-red-50"
                        }`}>
                          <i className={`${
                            t.type === "儲值"
                              ? "ri-arrow-down-line text-emerald-600"
                              : t.type === "體驗金回收"
                              ? "ri-arrow-left-right-line text-amber-600"
                              : "ri-arrow-up-line text-red-500"
                          } text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-sm text-gray-800">{t.description || ""}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{fmtDate(t.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${(Number(t.amount) || 0) > 0 ? "text-emerald-600" : "text-red-500"}`}>
                          {(Number(t.amount) || 0) > 0 ? "+" : "-"}${Math.abs(Number(t.amount) || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400">餘額 ${toMoney(t.balance).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Orders */}
          {activeTab === "orders" && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-900">全部訂單</h2>
              </div>
              {isLoading ? (
                <div className="px-5 py-8 text-center text-gray-400 text-sm">載入中...</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {orders.map((order) => (
                    <div key={order.id} className="px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{order.id}</p>
                          <p className="text-xs text-gray-400">{fmtDate(order.created_at)}</p>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                          order.status === "待發貨"
                            ? "bg-amber-100 text-amber-700"
                            : order.status === "已發貨"
                            ? "bg-blue-100 text-blue-700"
                            : order.status === "已完成"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-600"
                        }`}>
                          {order.status === "待發貨" ? "已訂貨" : order.status === "已發貨" ? "已上架" : order.status === "已完成" ? "銷售完畢" : order.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">合計 ${toMoney(order.total_amount).toLocaleString()}</span>
                        {order.tracking_number && (
                          <span className="text-xs text-gray-400">物流：{order.tracking_number}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}